import { useCallback, useState } from 'react';
import { useChangePassword } from './useChangePassword';
import { cognitoSignIn } from '../../pages/login/cognito';
import config from '../../config';

type ModalStep = 'form' | 'success';

interface UseChangePasswordModalReturn {
  // Step management
  step: ModalStep;

  // Form state
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
  passwordValid: boolean;

  // Form handlers
  setCurrentPassword: (value: string) => void;
  setNewPassword: (value: string) => void;
  setConfirmPassword: (value: string) => void;
  setPasswordValid: (valid: boolean) => void;

  // Messages
  errorMessage: string;

  // Operations
  handleChangePassword: (userEmail: string) => Promise<void>;

  // Modal lifecycle
  resetModal: () => void;

  // Status
  isProcessing: boolean;
}

/**
 * Hook to manage change password modal flow.
 * Platform-agnostic: Can be used in React Native.
 *
 * Manages:
 * - Form state for password fields
 * - Password validation state
 * - Error messages
 * - Integration with useChangePassword for API calls
 *
 * @returns Change password modal state, handlers, and operations
 */
export function useChangePasswordModal(): UseChangePasswordModalReturn {
  const [step, setStep] = useState<ModalStep>('form');
  const [currentPassword, setCurrentPasswordState] = useState('');
  const [newPassword, setNewPasswordState] = useState('');
  const [confirmPassword, setConfirmPasswordState] = useState('');
  const [passwordValid, setPasswordValid] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const changePasswordHook = useChangePassword();

  const resetModal = useCallback(() => {
    setStep('form');
    setCurrentPasswordState('');
    setNewPasswordState('');
    setConfirmPasswordState('');
    setPasswordValid(false);
    setErrorMessage('');
  }, []);

  const handleChangePassword = useCallback(
    async (userEmail: string) => {
      if (!currentPassword) {
        setErrorMessage('Please enter your current password');
        return;
      }

      if (!passwordValid) {
        setErrorMessage('Please ensure your new password meets all requirements');
        return;
      }

      setErrorMessage('');

      // First, authenticate with current password to get access token
      const authResult = await cognitoSignIn(
        config.cognito.region,
        config.cognito.clientId,
        userEmail,
        currentPassword,
      );

      if (!authResult.success || !authResult.data.AccessToken) {
        setErrorMessage('Current password is incorrect');
        return;
      }

      // Now use the access token to change the password
      const result = await changePasswordHook.changePassword(
        authResult.data.AccessToken,
        currentPassword,
        newPassword,
      );

      if (result.success) {
        setStep('success');
      } else {
        setErrorMessage(result.error || 'Failed to change password. Please try again.');
      }
    },
    [currentPassword, passwordValid, newPassword, changePasswordHook],
  );

  return {
    step,
    currentPassword,
    newPassword,
    confirmPassword,
    passwordValid,
    setCurrentPassword: useCallback((value: string) => {
      setCurrentPasswordState(value);
      setErrorMessage('');
    }, []),
    setNewPassword: useCallback((value: string) => {
      setNewPasswordState(value);
      setErrorMessage('');
    }, []),
    setConfirmPassword: useCallback((value: string) => {
      setConfirmPasswordState(value);
      setErrorMessage('');
    }, []),
    setPasswordValid,
    errorMessage,
    handleChangePassword,
    resetModal,
    isProcessing: changePasswordHook.isProcessing,
  };
}
